/**
 * ΜΙΑ websocket σύνδεση ανά καρτέλα — κοινή για όλη την εφαρμογή.
 *
 * ΓΙΑΤΙ: το useSocket ήταν απλό hook, οπότε ΚΑΘΕ component που το καλούσε άνοιγε
 * ΔΙΚΗ ΤΟΥ σύνδεση. Στο ταμείο είναι ταυτόχρονα φορτωμένα τέσσερα τέτοια
 * (IncomingCallPopup, GlobalPosPreloadToast, GlobalPrintHealth, IncomingOrderQueue),
 * άρα 4 συνδέσεις ανά καρτέλα — μετρημένο ζωντανά. Και επειδή ήταν δεμένες με τον
 * κύκλο ζωής των components, κάθε αλλαγή σελίδας τις έριχνε και τις ξαναέχτιζε:
 * στο κατάστημα Ελληνικού μετρήθηκαν 124 συνδέσεις σε μία ώρα από ΕΝΑ τερματικό.
 *
 * ΤΟ ΠΡΑΓΜΑΤΙΚΟ ΚΟΣΤΟΣ: στο παράθυρο που μια σύνδεση ξαναχτιζόταν, το μήνυμα
 * 'new-order' χανόταν. Η παραγγελία έμπαινε στη βάση αλλά το popup δεν εμφανιζόταν
 * ποτέ — γι' αυτό «κλείσε και ξανάνοιξε την εφαρμογή» το διόρθωνε (στην εκκίνηση
 * γίνεται HTTP ερώτημα για τις εκκρεμείς).
 *
 * ΔΥΟ ΛΕΠΤΟΜΕΡΕΙΕΣ ΠΟΥ ΕΧΟΥΝ ΣΗΜΑΣΙΑ:
 *  - Το socket κρατιέται σε STATE, όχι σε ref. Στο παλιό hook επιστρεφόταν από ref,
 *    οπότε όταν η σύνδεση ξαναχτιζόταν οι καταναλωτές κρατούσαν το ΠΑΛΙΟ (νεκρό)
 *    αντικείμενο μέχρι να τύχει κάποιο re-render, και οι listeners τους ήταν
 *    κολλημένοι σε πεθαμένο socket.
 *  - Η εξάρτηση είναι store?.id (πρωτογενής τιμή), όχι το αντικείμενο store. Με το
 *    αντικείμενο, κάθε ανανέωση του auth context ξανάχτιζε τη σύνδεση χωρίς λόγο.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../components/config/api';
import { CLIENT_BUILD } from '../utils/clientBuild';

export interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  error: string | null;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  isConnected: false,
  error: null,
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, store } = useAuth();
  const storeId = store?.id;

  useEffect(() => {
    if (!isAuthenticated || !storeId) {
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const serverUrl = API_BASE_URL.replace('/api', '');
    const token = localStorage.getItem('xentrix_token');
    const s = io(serverUrl, {
      // build: ο server καταγράφει ποιο bundle κρατά ανοιχτή σύνδεση (surface
      // 'socket') — ένα ταμείο χωρίς F5 μπορεί να μην κάνει HTTP για ώρες.
      auth: { token, build: CLIENT_BUILD },
      transports: ['websocket', 'polling'],
    });

    s.on('connect', () => {
      setIsConnected(true);
      setError(null);
      // Το join γίνεται σε ΚΑΘΕ connect, όχι μόνο στην πρώτη φορά: μετά από
      // αυτόματη επανασύνδεση ο server μας έχει βγάλει από το δωμάτιο.
      s.emit('join-store', storeId);
    });
    s.on('disconnect', () => setIsConnected(false));
    s.on('connect_error', (err: any) => {
      console.error('❌ WebSocket connection error:', err?.message);
      setError(err?.message || 'connect_error');
      setIsConnected(false);
    });
    s.on('error', (err: any) => {
      console.error('❌ WebSocket error:', err?.message);
      setError(err?.message || 'error');
    });

    setSocket(s);
    return () => {
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, storeId]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, error }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = (): SocketContextValue => useContext(SocketContext);

export default SocketContext;
